import type { StoreApi } from "zustand";
import { clearOnSignOut } from "./signOutCleanup";

/**
 * A person's own work in progress in this browser: Marvin's current thread and conversation
 * anchors, a submittal package's unsent note, Marvin run and filing decisions, detector accepts
 * waiting to be sent again, and opening-annotation drafts. It is kept under the project key
 * (`localScopeSlug`) and the admitted member, as admin-chat pointers (`adminChatStorageKey`) and
 * recent searches are, so the next person on a shared browser never reads it, however the
 * previous session ended (a sign-out, or a project tab's expiry, which runs none), and one entity
 * id in two cloned tenants never shares an entry. Sign-out deletes it all (`utils/logout.ts`):
 * every entry's key starts with `PERSONAL_PREFIX`, and `PERSONAL_DATABASES` hold nothing else.
 *
 * Null until both the project and the member are known: nothing is read or written then.
 */
export function personalScope(
    projectKey: string | null,
    memberId: string | null | undefined,
): string | null {
    return projectKey && memberId ? `${projectKey}:${memberId}` : null;
}

/** Every person's entry in local and session storage starts with it. */
export const PERSONAL_PREFIX = "pp.personal:";

/** The opening-annotation drafts (`openingAnnotationStore`), each under its person's scope. */
export const OPENING_DRAFTS_DATABASE = "opening-annotation";
/** The model bundle cache (`components/models/viewer/bundleCache.ts`): content-addressed bytes, deleted at sign-out. */
export const BUNDLE_CACHE_DATABASE = "model-bundle-cache";
/** IndexedDB databases sign-out deletes: people's work in progress, and the model bundle cache (`bundleCache.ts`). */
export const PERSONAL_DATABASES: readonly string[] = [
    OPENING_DRAFTS_DATABASE,
    BUNDLE_CACHE_DATABASE,
];

/** Where `name`'s entry for the person in `scope` lives, for one entity when one is given; null
 *  without a scope. */
export function personalKey(
    name: string,
    scope: string | null,
    ...entity: string[]
): string | null {
    return scope ? `${PERSONAL_PREFIX}${[name, scope, ...entity].join(":")}` : null;
}

/** An entry's stored JSON, or null when there is none, storage refuses, or it does not parse. */
export function readPersonal(storage: () => Storage, key: string | null): unknown {
    if (!key) return null;
    try {
        const raw = storage().getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

/** Stores `value` under `key` (null deletes it); without a key, or where storage refuses, the
 *  value lives in the page only. */
export function writePersonal(storage: () => Storage, key: string | null, value: unknown): void {
    if (!key) return;
    try {
        if (value === null) storage().removeItem(key);
        else storage().setItem(key, JSON.stringify(value));
    } catch {
        // Storage refused (a private window, quota): the value lives in the page only.
    }
}

/** The scope the module-level stores are on (`bindPersonalScope`). */
let bound: string | null = null;
const binders = new Set<(scope: string | null) => void>();

/**
 * Moves every store kept with `keepPersonalState` to the person and project the app now shows
 * (`PersonalScopeBinding`); null leaves them on nobody.
 */
export function bindPersonalScope(scope: string | null): void {
    if (scope === bound) return;
    bound = scope;
    for (const bind of binders) bind(scope);
}

/** The person and project the app shows (`bindPersonalScope`), for stores keyed per call
 *  rather than kept with `keepPersonalState`; null when nobody is. */
export function boundPersonalScope(): string | null {
    return bound;
}

// Sign-out forgets the person in this page before their stored entries are deleted.
clearOnSignOut(() => bindPersonalScope(null));

interface PersonalPart<S> {
    /** The entry's name (`personalKey`). */
    name: string;
    storage: () => Storage;
    /** The person's part of the state, as stored; null stores nothing. */
    save: (state: S) => unknown;
    /** The person's part of the state from what is stored for them: null when nothing is, or
     *  JSON that must be checked. */
    load: (stored: unknown) => Partial<S>;
}

/**
 * Keeps the person's part of a module-level store under the bound scope. When the scope moves
 * the store drops that part and takes the new person's stored one, and each change of it is
 * written under the scope it was made in. Unbound, the part is empty and nothing is read or
 * written, so a store never starts from an entry nobody has claimed.
 */
export function keepPersonalState<S>(store: StoreApi<S>, part: PersonalPart<S>): void {
    const { name, storage, save, load } = part;
    binders.add((scope) => store.setState(load(readPersonal(storage, personalKey(name, scope)))));
    store.subscribe((state, previous) => {
        const next = save(state);
        if (JSON.stringify(next) === JSON.stringify(save(previous))) return;
        writePersonal(storage, personalKey(name, bound), next);
    });
    store.setState(load(readPersonal(storage, personalKey(name, bound))));
}
